import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from '@/shared/components/Dialog';
import { DatePickerField } from '@/shared/components/DatePickerField';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { TextField } from '@/shared/components/TextField';
import { Tooltip } from '@/shared/components/Tooltip';
import { ASSIGN_TECHNICIAN_LABELS as L } from '../../constants/labels';
import type { AssignToRole } from '../../hooks/useAssignTechnician';
import { EligibleTechnicianDropdown } from './EligibleTechnicianDropdown';
import { TaskFormDropdown } from './TaskFormDropdown';

interface AssignTechnicianModalProps {
  open: boolean;
  onClose: () => void;

  serviceRequestId: string | undefined;

  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  startDateIso: string;
  endDateIso: string;
  onStartDateChange: (d: Date | undefined) => void;
  onEndDateChange: (d: Date | undefined) => void;
  onStartTimeChange: (t: string) => void;
  onEndTimeChange: (t: string) => void;
  startDateError: string;
  endDateError: string;
  endTimeError: string;
  windowComplete: boolean;

  selectedTaskFormId: string;
  selectedTaskFormFormId: string;
  selectedTaskFormName: string;
  onSelectTaskForm: (
    id: string,
    formId: string,
    name: string,
    certificate: { id: string; cert_template_id: string; name: string } | null
  ) => void;
  taskFormError: string;
  linkedCertificateName: string;
  linkedCertificateId: string;
  noCertificateError: string;

  assignToRole: AssignToRole;
  onAssignToRoleChange: (role: AssignToRole) => void;
  assignToError: string;

  selectedTechnicianId: string;
  selectedTechnicianCode: string;
  onSelectTechnician: (id: string, technicianCode: string) => void;
  technicianError: string;

  selectedAdminId: string;
  selectedAdminCode: string;
  onSelectAdmin: (id: string, adminCode: string) => void;
  adminError: string;

  isSubmitting: boolean;
  submitError: string;
  canSubmit: boolean;
  onSubmit: () => void;
}

const OptionalSuffix: React.FC = () => (
  <span
    style={{
      fontFamily: 'Roboto',
      fontSize: 12,
      lineHeight: '16px',
      fontWeight: 400,
      letterSpacing: 0.4,
      color: '#474A4C',
      marginLeft: 4,
    }}
  >
    {L.FIELD_TIME_OPTIONAL}
  </span>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h3
    style={{
      fontFamily: 'Roboto',
      fontSize: 18,
      lineHeight: '24px',
      fontWeight: 400,
      letterSpacing: 0.1,
      color: '#474A4C',
    }}
  >
    {children}
  </h3>
);

const ClockIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#181A1B" strokeWidth="2" />
    <path
      d="M12 7v5l3 2"
      stroke="#181A1B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const parseTime = (value: string): { hour: number; minute: number } => {
  const [hStr = '0', mStr = '0'] = (value || '').split(':');
  const hour = Math.max(0, Math.min(23, Number(hStr) || 0));
  const minute = Math.max(0, Math.min(59, Number(mStr) || 0));
  return { hour, minute };
};

const formatTime = (hour: number, minute: number): string => {
  const h = String(hour).padStart(2, '0');
  const m = String(minute).padStart(2, '0');
  return `${h}:${m}`;
};

const TimeNumberBox: React.FC<{
  value: number;
  max: number;
  active: boolean;
  caption: string;
  ariaLabel: string;
  testId: string;
  onChange: (next: number) => void;
  onFocus: () => void;
}> = ({
  value,
  max,
  active,
  caption,
  ariaLabel,
  testId,
  onChange,
  onFocus,
}) => {
  const [text, setText] = useState(() => String(value).padStart(2, '0'));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setText(String(value).padStart(2, '0'));
    }
  }, [value]);

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    if (digits === '') {
      setText('');
      onChange(0);
      return;
    }
    const num = Math.min(max, Number(digits));
    setText(String(num));
    onChange(num);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    focusedRef.current = true;
    e.target.select();
    onFocus();
  };

  const handleBlur = () => {
    focusedRef.current = false;
    setText(String(value).padStart(2, '0'));
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={text}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(e) => handleChange(e.target.value)}
        aria-label={ariaLabel}
        data-testid={testId}
        className="w-22 text-center outline-none"
        style={{
          height: 50,
          borderRadius: 8,
          backgroundColor: active ? '#E8EEF2' : '#F2F4F5',
          border: active ? '2px solid #2A4455' : '2px solid transparent',
          fontFamily: 'Roboto',
          fontSize: 24,
          lineHeight: '32px',
          fontWeight: 400,
          color: '#181A1B',
          letterSpacing: 0,
        }}
      />
      <span
        style={{
          fontFamily: 'Roboto',
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 400,
          letterSpacing: 0.4,
          color: '#474A4C',
        }}
      >
        {caption}
      </span>
    </div>
  );
};

const TimeField: React.FC<{
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  testId?: string;
}> = ({
  label,
  value,
  onChange,
  disabled = false,
  error = false,
  errorMessage,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [draftHour, setDraftHour] = useState(0);
  const [draftMinute, setDraftMinute] = useState(0);
  const [activeField, setActiveField] = useState<'hour' | 'minute'>('hour');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { hour, minute } = parseTime(value);
  const displayValue = value ? formatTime(hour, minute) : '';
  const ariaLabel = typeof label === 'string' ? label : undefined;

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current) {
        return;
      }
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const openPopup = () => {
    if (disabled) {
      return;
    }
    setDraftHour(hour);
    setDraftMinute(minute);
    setActiveField('hour');
    setOpen(true);
  };

  const closePopup = () => setOpen(false);

  const confirm = () => {
    onChange(formatTime(draftHour, draftMinute));
    setOpen(false);
  };

  return (
    <div className="flex-1 relative" ref={wrapperRef}>
      <div
        className={`relative w-full h-14 bg-[#E5E7E9] rounded-lg flex items-center pl-4 pr-3 gap-2 transition-colors ${
          disabled
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:bg-[#eceeef] cursor-pointer'
        }`}
        style={{
          borderBottom: error
            ? '2px solid #E11B1B'
            : open
              ? '2px solid #2a4455'
              : 'none',
        }}
        onClick={openPopup}
        onKeyDown={(e) => {
          if (disabled) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPopup();
          }
        }}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        data-testid={testId}
      >
        <div
          className="flex-1 relative flex flex-col justify-center"
          style={{ minHeight: 40 }}
        >
          {(() => {
            const labelFloating = open || Boolean(value);
            const labelColor = open ? '#2a4455' : '#474A4C';
            return (
              <span
                className="absolute left-0 pointer-events-none origin-top-left"
                style={{
                  fontFamily: 'Roboto',
                  color: labelColor,
                  top: labelFloating ? 0 : 10,
                  fontSize: labelFloating ? 12 : 16,
                  lineHeight: labelFloating ? '16px' : '20px',
                  fontWeight: 400,
                  letterSpacing: 0.25,
                  transition:
                    'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                }}
              >
                {label}
              </span>
            );
          })()}
          {displayValue && (
            <span
              className="self-start"
              style={{
                fontFamily: 'Roboto',
                color: '#181A1B',
                fontSize: 16,
                lineHeight: '20px',
                fontWeight: 400,
                letterSpacing: 0.25,
                marginTop: 16,
              }}
            >
              {displayValue}
            </span>
          )}
        </div>
        {!open && !error && (
          <div
            className="absolute bottom-0"
            style={{
              left: '6px',
              right: '6px',
              height: '1px',
              backgroundColor: '#6a7278',
            }}
          />
        )}
        <div className="p-2 rounded-full hover:bg-black/5 transition-colors shrink-0">
          <ClockIcon />
        </div>
      </div>

      {open && (
        <div
          className="absolute left-0 right-0 mt-2 z-50 bg-white rounded-2xl"
          style={{
            top: '100%',
            padding: 16,
            border: '1px solid #DCDDE0',
            boxShadow: '0 4px 12px 0 rgba(0,0,0,0.12)',
          }}
          role="dialog"
          aria-label={L.TIME_DIALOG_TITLE}
          data-testid={testId ? `${testId}-popup` : undefined}
        >
          <p
            style={{
              fontFamily: 'Roboto',
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 400,
              letterSpacing: 0.4,
              color: '#474A4C',
              marginBottom: 20,
            }}
          >
            {L.TIME_DIALOG_TITLE}
          </p>
          <div className="flex items-center justify-center gap-2">
            <TimeNumberBox
              value={draftHour}
              max={23}
              active={activeField === 'hour'}
              caption={L.TIME_DIALOG_HOUR}
              ariaLabel={L.TIME_DIALOG_HOUR}
              testId={testId ? `${testId}-hour-input` : 'time-hour-input'}
              onChange={setDraftHour}
              onFocus={() => setActiveField('hour')}
            />
            <span
              style={{
                fontFamily: 'Roboto',
                fontSize: 24,
                lineHeight: '32px',
                fontWeight: 400,
                color: '#181A1B',
                paddingBottom: 20,
              }}
            >
              :
            </span>
            <TimeNumberBox
              value={draftMinute}
              max={59}
              active={activeField === 'minute'}
              caption={L.TIME_DIALOG_MINUTE}
              ariaLabel={L.TIME_DIALOG_MINUTE}
              testId={testId ? `${testId}-minute-input` : 'time-minute-input'}
              onChange={setDraftMinute}
              onFocus={() => setActiveField('minute')}
            />
          </div>
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={closePopup}
              className="h-9 px-3 rounded-md text-sm font-medium tracking-[0.1px] text-[#2A4455] hover:bg-[#F3F4F6] transition-colors cursor-pointer"
              data-testid={testId ? `${testId}-cancel` : 'time-cancel'}
            >
              {L.BUTTON_CANCEL}
            </button>
            <button
              type="button"
              onClick={confirm}
              className="h-9 px-3 rounded-md text-sm font-medium tracking-[0.1px] text-[#2A4455] hover:bg-[#F3F4F6] transition-colors cursor-pointer"
              data-testid={testId ? `${testId}-ok` : 'time-ok'}
            >
              {L.TIME_DIALOG_OK}
            </button>
          </div>
        </div>
      )}
      {error && errorMessage && (
        <p
          className="mt-1"
          style={{
            fontFamily: 'Roboto',
            fontSize: 12,
            lineHeight: '16px',
            letterSpacing: 0.4,
            color: '#E11B1B',
          }}
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

const AssignTechnicianModal: React.FC<AssignTechnicianModalProps> = ({
  open,
  onClose,
  serviceRequestId,
  startDate,
  endDate,
  startTime,
  endTime,
  startDateIso,
  endDateIso,
  onStartDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
  startDateError,
  endDateError,
  endTimeError,
  windowComplete,
  selectedTaskFormId,
  selectedTaskFormFormId,
  onSelectTaskForm,
  taskFormError,
  linkedCertificateName,
  linkedCertificateId,
  noCertificateError,
  assignToRole,
  onAssignToRoleChange,
  assignToError,
  selectedTechnicianId,
  selectedTechnicianCode,
  onSelectTechnician,
  technicianError,
  selectedAdminId,
  selectedAdminCode,
  onSelectAdmin,
  adminError,
  isSubmitting,
  submitError,
  canSubmit,
  onSubmit,
}) => {
  const windowReady = windowComplete && !endDateError && !endTimeError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      className="overflow-visible!"
      data-testid="assign-technician-modal"
    >
      <div className="flex flex-col">
        <div className="px-6 pt-6 pb-0 sticky top-0 bg-neutral-100 z-10 rounded-t-xl">
          <h2
            style={{
              fontFamily: 'Roboto',
              fontSize: 22,
              lineHeight: '28px',
              fontWeight: 400,
              letterSpacing: 0,
              color: '#181A1B',
            }}
            data-testid="assign-technician-title"
          >
            {L.TITLE}
          </h2>
        </div>

        <div className="px-6 py-4 flex flex-col gap-8 max-h-[calc(100vh-48px)] overflow-y-visible">
          {/* Schedule details */}
          <div className="flex flex-col gap-2">
            <SectionHeading>{L.SECTION_SCHEDULE}</SectionHeading>
            <div className="flex gap-3">
              <DatePickerField
                label={L.FIELD_START_DATE}
                value={startDate}
                onChange={onStartDateChange}
                variant="flat"
                labelFontSize={16}
                disabled={isSubmitting}
                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                ariaLabel={L.FIELD_START_DATE}
                error={Boolean(startDateError)}
                errorMessage={startDateError || undefined}
                dataTestId="assign-technician-start-date"
              />
              <DatePickerField
                label={L.FIELD_END_DATE}
                value={endDate}
                onChange={onEndDateChange}
                variant="flat"
                labelFontSize={16}
                disabled={isSubmitting}
                minDate={
                  startDate
                    ? new Date(new Date(startDate).setHours(0, 0, 0, 0))
                    : new Date(new Date().setHours(0, 0, 0, 0))
                }
                ariaLabel={L.FIELD_END_DATE}
                error={Boolean(endDateError)}
                errorMessage={endDateError || undefined}
                dataTestId="assign-technician-end-date"
              />
            </div>
            <div className="flex gap-3" style={{ marginTop: 24 }}>
              <TimeField
                label={
                  <>
                    {L.FIELD_START_TIME}
                    <OptionalSuffix />
                  </>
                }
                value={startTime}
                onChange={onStartTimeChange}
                disabled={isSubmitting}
                testId="assign-technician-start-time"
              />
              <TimeField
                label={
                  <>
                    {L.FIELD_END_TIME}
                    <OptionalSuffix />
                  </>
                }
                value={endTime}
                onChange={onEndTimeChange}
                disabled={isSubmitting}
                error={Boolean(endTimeError)}
                {...(endTimeError ? { errorMessage: endTimeError } : {})}
                testId="assign-technician-end-time"
              />
            </div>

            {/* Row 1: Task Form name | Task Form ID */}
            <div
              className="grid grid-cols-2 gap-3 items-start"
              style={{ marginTop: 24 }}
            >
              <div className="min-w-0">
                <TaskFormDropdown
                  label={L.FIELD_TASK_FORM}
                  selectedId={selectedTaskFormId}
                  onSelect={onSelectTaskForm}
                  disabled={isSubmitting}
                  error={Boolean(taskFormError)}
                  {...(taskFormError ? { errorMessage: taskFormError } : {})}
                />
              </div>
              {selectedTaskFormId && (
                <div className="min-w-0">
                  <TextField
                    label={L.FIELD_TASK_FORM_ID}
                    value={selectedTaskFormFormId}
                    disabled
                    fullWidth
                    dataTestId="assign-task-form-id"
                  />
                </div>
              )}
            </div>

            {/* Row 2: Certificate Template Name | Certificate Template ID */}
            {selectedTaskFormId && linkedCertificateName && (
              <div
                className="grid grid-cols-2 gap-3 items-start"
                style={{ marginTop: 12 }}
              >
                <Tooltip
                  content={linkedCertificateName}
                  side="bottom"
                  align="left"
                  triggerTabIndex={-1}
                  forceShow
                >
                  <div className="min-w-0 [&_input]:text-ellipsis">
                    <TextField
                      label={L.FIELD_CERTIFICATE_TEMPLATE_NAME}
                      value={linkedCertificateName}
                      disabled
                      fullWidth
                      dataTestId="assign-certificate-name"
                    />
                  </div>
                </Tooltip>
                {linkedCertificateId && (
                  <Tooltip
                    content={linkedCertificateId}
                    side="bottom"
                    align="left"
                    triggerTabIndex={-1}
                    forceShow
                  >
                    <div className="min-w-0 [&_input]:text-ellipsis">
                      <TextField
                        label={L.FIELD_CERTIFICATE_TEMPLATE_ID}
                        value={linkedCertificateId}
                        disabled
                        fullWidth
                        dataTestId="assign-certificate-template-id"
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            )}

            {selectedTaskFormId && noCertificateError && (
              <div style={{ marginTop: 8 }}>
                <p
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: '16px',
                    letterSpacing: 0.4,
                    color: '#474A4C',
                  }}
                  data-testid="assign-no-certificate-error"
                >
                  {noCertificateError}
                </p>
                <Link
                  to="/Certyfikaty/Utwórz-szablon-certyfikatu"
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                    letterSpacing: 0.1,
                    color: '#2A4455',
                    textDecoration: 'underline',
                    textDecorationStyle: 'solid',
                  }}
                  data-testid="assign-create-certificate-link"
                >
                  {L.CREATE_CERTIFICATE_LINK}
                </Link>
              </div>
            )}
          </div>

          <div className="h-px bg-[#DCDDE0]" aria-hidden="true" />

          {/* Assignee */}
          <div
            className="flex flex-col gap-3"
            data-field="assign-assignee-picker"
          >
            <SectionHeading>{L.SECTION_ASSIGNEE}</SectionHeading>

            {/* Row 1: assignee-type toggle (left) + role-specific name picker
                (right). The toggle starts unselected and stays disabled until a
                valid window is chosen; the name picker only appears once a role
                is chosen. */}
            <div className="grid grid-cols-2 gap-3 items-start">
              <div className="min-w-0">
                {(() => {
                  const assignToDisabled = isSubmitting || !windowReady;
                  const assignToDropdown = (
                    <Dropdown
                      menuFullWidth
                      placement="bottom-start"
                      items={[
                        {
                          id: 'admin',
                          label: L.ASSIGN_TO_ADMIN,
                          selected: assignToRole === 'admin',
                        },
                        {
                          id: 'technician',
                          label: L.ASSIGN_TO_TECHNICIAN,
                          selected: assignToRole === 'technician',
                        },
                      ]}
                      {...(assignToRole ? { selectedId: assignToRole } : {})}
                      onItemClick={(id) =>
                        onAssignToRoleChange(id as AssignToRole)
                      }
                      disabled={assignToDisabled}
                      error={Boolean(assignToError)}
                      {...(assignToError
                        ? { errorMessage: assignToError }
                        : {})}
                      trigger={({ open, toggle, disabled: d, error: e }) => (
                        <DropDownButton
                          label={L.FIELD_ASSIGN_TO}
                          value={
                            assignToRole === 'technician'
                              ? L.ASSIGN_TO_TECHNICIAN
                              : assignToRole === 'admin'
                                ? L.ASSIGN_TO_ADMIN
                                : ''
                          }
                          open={open}
                          toggle={toggle}
                          disabled={d}
                          error={e}
                          data-testid="assign-assign-to"
                        />
                      )}
                    />
                  );
                  if (!isSubmitting && !windowReady) {
                    return (
                      <Tooltip
                        content={L.ADMIN_TOOLTIP_NO_WINDOW}
                        forceShow
                        side="bottom"
                        align="left"
                        cursor="not-allowed"
                      >
                        {assignToDropdown}
                      </Tooltip>
                    );
                  }
                  return assignToDropdown;
                })()}
              </div>

              {assignToRole === 'admin' && (
                <div className="min-w-0">
                  <EligibleTechnicianDropdown
                    serviceRequestId={serviceRequestId}
                    startDate={startDateIso}
                    endDate={endDateIso}
                    startTime={startTime}
                    endTime={endTime}
                    windowReady={windowReady}
                    selectedId={selectedAdminId}
                    onSelect={onSelectAdmin}
                    label={L.FIELD_ADMIN}
                    assigneeType="admin"
                    disabled={isSubmitting}
                    error={windowReady && Boolean(adminError)}
                    {...(windowReady && adminError
                      ? { errorMessage: adminError }
                      : {})}
                  />
                </div>
              )}
              {assignToRole === 'technician' && (
                <div className="min-w-0">
                  <EligibleTechnicianDropdown
                    serviceRequestId={serviceRequestId}
                    startDate={startDateIso}
                    endDate={endDateIso}
                    startTime={startTime}
                    endTime={endTime}
                    windowReady={windowReady}
                    selectedId={selectedTechnicianId}
                    onSelect={onSelectTechnician}
                    label={L.FIELD_TECHNICIAN}
                    disabled={isSubmitting}
                    error={windowReady && Boolean(technicianError)}
                    {...(windowReady && technicianError
                      ? { errorMessage: technicianError }
                      : {})}
                  />
                </div>
              )}
            </div>

            {/* Row 2: selected assignee's business id in the left column. */}
            {assignToRole === 'admin' && selectedAdminId && (
              <div className="grid grid-cols-2 gap-3 items-start">
                <div className="min-w-0">
                  <TextField
                    label={L.FIELD_ADMIN_ID}
                    value={selectedAdminCode}
                    disabled
                    fullWidth
                    dataTestId="assign-admin-id"
                  />
                </div>
              </div>
            )}
            {assignToRole === 'technician' && selectedTechnicianId && (
              <div className="grid grid-cols-2 gap-3 items-start">
                <div className="min-w-0">
                  <TextField
                    label={L.FIELD_TECHNICIAN_ID}
                    value={selectedTechnicianCode}
                    disabled
                    fullWidth
                    dataTestId="assign-technician-id"
                  />
                </div>
              </div>
            )}
          </div>

          {submitError && (
            <p
              style={{
                fontFamily: 'Roboto',
                fontSize: 12,
                lineHeight: '16px',
                letterSpacing: 0.4,
                color: '#E11B1B',
              }}
              data-testid="assign-technician-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 sticky bottom-0 bg-neutral-100 z-10 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="assign-technician-cancel"
          >
            {L.BUTTON_CANCEL}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              canSubmit
                ? 'hover:bg-[#1f3340] cursor-pointer'
                : 'opacity-50 cursor-not-allowed'
            }`}
            data-testid="assign-technician-submit"
          >
            {isSubmitting ? L.BUTTON_SUBMITTING : L.BUTTON_SUBMIT}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AssignTechnicianModal;
